import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/community/ChatList";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Phone, MonitorUp, Monitor } from "lucide-react";

const Timer = ({ running }) => {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => setS((v) => v + 1), 1000);
    return () => clearInterval(i);
  }, [running]);
  return <span data-testid="call-timer">{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</span>;
};

const RoundBtn = ({ onClick, active, danger, icon: Icon, label, testId }) => (
  <button data-testid={testId} onClick={onClick} title={label} aria-label={label}
    className="w-14 h-14 rounded-full flex items-center justify-center border transition-transform duration-200 hover:scale-110 active:scale-95"
    style={{
      background: danger ? "linear-gradient(135deg,#B4560E,#2A1403)"
        : active ? "rgba(255,42,42,0.28)" : "rgba(255,255,255,0.08)",
      borderColor: danger ? "rgba(255,190,130,0.6)" : "rgba(255,255,255,0.18)",
      backdropFilter: "blur(16px)",
      boxShadow: danger ? "0 0 26px rgba(255,42,42,0.45)" : "none",
    }}>
    <Icon className="w-5 h-5 text-white" />
  </button>
);

/** Pantalla de llamada: timbre entrante, video y controles. */
export const CallOverlay = ({ call, localStream, remoteStream,
                              onAccept, onReject, onHangup,
                              onToggleMute, onToggleCam, onToggleShare }) => {
  const localRef = useRef(null);
  const remoteRef = useRef(null);

  useEffect(() => {
    if (localRef.current) localRef.current.srcObject = localStream || null;
  }, [localStream, call?.status]);
  useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = remoteStream || null;
  }, [remoteStream, call?.status]);

  if (!call) return null;
  const isVideo = call.kind === "video";
  const name = call.peer?.name || "Miembro";

  if (call.status === "incoming") {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        style={{ background: "rgba(3,0,0,0.78)", backdropFilter: "blur(6px)" }}
        data-testid="incoming-call">
        <div className="glass-card p-7 w-full max-w-sm text-center call-ring">
          <div className="flex justify-center mb-4"><Avatar person={call.peer} size={78} /></div>
          <div className="text-white text-lg font-semibold">{name}</div>
          <div className="text-xs text-white/55 mt-1 uppercase tracking-[0.18em]">
            {isVideo ? "Videollamada entrante" : "Llamada entrante"}
          </div>
          <div className="flex items-center justify-center gap-7 mt-8">
            <RoundBtn testId="call-reject-btn" danger icon={PhoneOff} label="Rechazar" onClick={onReject} />
            <button data-testid="call-accept-btn" onClick={onAccept} aria-label="Contestar"
              className="w-14 h-14 rounded-full flex items-center justify-center border border-emerald-300/50 transition-transform hover:scale-110"
              style={{ background: "linear-gradient(135deg,#17C964,#0B7A3C)", boxShadow: "0 0 26px rgba(23,201,100,0.4)" }}>
              <Phone className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" style={{ background: "rgba(3,0,0,0.9)" }}
      data-testid="call-overlay">
      <div className="relative flex-1 overflow-hidden">
        {isVideo && remoteStream ? (
          <video ref={remoteRef} autoPlay playsInline data-testid="call-remote-video"
            className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <div className={call.status === "outgoing" ? "call-ring rounded-full" : ""}>
              <Avatar person={call.peer} size={96} />
            </div>
            <div className="text-white text-lg font-semibold">{name}</div>
          </div>
        )}
        {!isVideo && remoteStream && <audio ref={remoteRef} autoPlay data-testid="call-remote-audio" />}

        {isVideo && localStream && (
          <video ref={localRef} autoPlay muted playsInline data-testid="call-local-video"
            className="absolute bottom-5 right-5 w-32 sm:w-44 rounded-2xl border border-red-500/40 object-cover"
            style={{ boxShadow: "0 0 24px rgba(255,42,42,0.3)" }} />
        )}

        <div className="absolute top-5 left-0 right-0 text-center text-xs text-white/70 tracking-[0.18em] uppercase"
          data-testid="call-status">
          {call.status === "outgoing" ? "Llamando…"
            : call.linked ? <Timer running /> : "Conectando…"}
        </div>
      </div>

      <div className="px-6 py-7 flex items-center justify-center gap-5 border-t border-white/10"
        style={{ background: "rgba(10,3,4,0.6)", backdropFilter: "blur(16px)" }}>
        <RoundBtn testId="call-mute-btn" active={call.muted} icon={call.muted ? MicOff : Mic}
          label={call.muted ? "Activar micrófono" : "Silenciar micrófono"} onClick={onToggleMute} />
        {isVideo && (
          <RoundBtn testId="call-cam-btn" active={call.camOff} icon={call.camOff ? VideoOff : Video}
            label={call.camOff ? "Encender cámara" : "Apagar cámara"} onClick={onToggleCam} />
        )}
        {isVideo && (
          <RoundBtn testId="call-share-btn" active={call.sharing}
            icon={call.sharing ? Monitor : MonitorUp}
            label={call.sharing ? "Dejar de compartir" : "Compartir pantalla"} onClick={onToggleShare} />
        )}
        <RoundBtn testId="call-hangup-btn" danger icon={PhoneOff} label="Colgar" onClick={onHangup} />
      </div>
    </div>
  );
};

export default CallOverlay;
